import { Fragment, memo, useMemo } from 'react';

import clsx from 'clsx';
import { Book } from 'lucide-react';

import Chip from '@mui/material/Chip';

import { UserGuideActionsColumn } from './userGuideTableColumns/UserGuideActionsColumn';
import { UserGuideCheckboxColumn } from './userGuideTableColumns/UserGuideCheckboxColumn';

import css from './UserGuidesTable.module.css';

import { Card } from 'v2/components/card/Card';
import { CardHeader } from 'v2/components/card/cardHeader/CardHeader';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { IPagination, Pageable, TableColumn } from 'v2/types';
import { UserGuideModel } from 'v2/types/UserGuideModel';
import { formatNumber } from 'v2/utils';

interface UserGuidesTableProps {
  data: Pageable<UserGuideModel>;
  pagination: IPagination;
  onRevalidate: VoidFunction;
}

export const UserGuidesTable = memo<UserGuidesTableProps>(({ data, pagination, onRevalidate }) => {
  const columns: TableColumn<UserGuideModel>[] = useMemo(
    () => [
      {
        key: 'guide',
        label: 'Guide',
        render: (row) => (
          <Fragment>
            <p className={clsx(css.userGuideTableKey, 'truncate')}>{row.key}</p>

            <Chip className="font-medium" color="secondary" label={row.type} size="small" />
          </Fragment>
        ),
      },
      {
        key: 'reward',
        label: 'Reward',
        render: (row) => <span className="font-medium">{formatNumber(row.reward)}&nbsp;FUNS</span>,
      },
      {
        key: 'isDefault',
        label: 'Is Default',
        align: 'center',
        render: (row) => <UserGuideCheckboxColumn guide={row} onRevalidate={onRevalidate} />,
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <UserGuideActionsColumn guide={row} onRevalidate={onRevalidate} />,
      },
    ],
    [onRevalidate]
  );

  return (
    <Card>
      <CardHeader title="All Guides" icon={Book} />

      <DataTable
        columns={columns}
        data={data.result}
        total={data.total}
        page={pagination.page}
        pageSize={pagination.pageSize}
        onPageChange={pagination.onPageChange}
        withPagination
      />
    </Card>
  );
});

UserGuidesTable.displayName = 'UserGuidesTable';
